<template>
  <div>
    <!-- Search Filters Section -->
    <q-expansion-item
      label="Search Filters"
      color="primary"
      switch-toggle-side
      expand-separator
      class="q-mb-md"
      :dense="true"
      expand-icon="expand_more"
      expand-icon-class="text-primary"
    >
      <div class="q-pa-md">
        <div class="row q-col-gutter-md q-mb-md">
          <!-- Customer Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.customer_id"
              :options="customerOptions"
              label="Customer"
              outlined
              dense
            />
          </div>

          <!-- Feedmill Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.feedmill_id"
              :options="feedmillOptions"
              label="Feedmill"
              outlined
              dense
            />
          </div>

          <!-- Driver Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.driver_id"
              :options="driverOptions"
              label="Driver"
              outlined
              dense
            />
          </div>
        </div>

        <div class="row q-col-gutter-md q-mb-md">
          <!-- Ticket Number Input -->
          <div class="col-4">
            <q-input
              v-model="filters.ticket_number"
              label="Ticket Number"
              outlined
              dense
            />
          </div>

          <!-- Dairy Ticket Number Input -->
          <div class="col-4">
            <q-input
              v-model="filters.dairy_ticket_number"
              label="Dairy Ticket Number"
              outlined
              dense
            />
          </div>

          <!-- Feed Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.feed_id"
              :options="feedOptions"
              label="Feed"
              outlined
              dense
            />
          </div>
        </div>

        <div class="row q-col-gutter-md">
          <!-- Date Range Picker -->
          <div class="col-4">
            <div class="clickable-text">
              <q-icon
                name="event"
                size="md"
                color="primary"
                class="cursor-pointer"
              />
              <span>Select Date Range</span>
            </div>
            <q-popup-proxy
              v-model="showFilterDatePicker"
              transition-show="scale"
              transition-hide="scale"
            >
              <q-date
                v-model="filters.dateRange"
                range
                :options="restrictDates"
                mask="YYYY-MM-DD"
                class="q-pa-md"
              >
                <div class="row items-center justify-end q-gutter-sm">
                  <q-btn
                    label="Clear"
                    color="primary"
                    @click="filters.dateRange = null"
                  />
                </div>
              </q-date>
            </q-popup-proxy>
          </div>
        </div>

        <!-- Apply & Reset Filters Buttons -->
        <div class="q-mt-md">
          <q-btn
            label="Apply Filters"
            color="primary"
            @click="fetchHaulTickets"
          />
          <q-btn
            label="Reset"
            color="primary"
            flat
            class="q-ml-sm"
            @click="clearFilters"
          />
        </div>
      </div>
    </q-expansion-item>

    <!-- Top Row with actions -->
    <div class="q-mt-md">
      <div class="row q-gutter-sm q-ml-md">
        <q-btn
          color="primary"
          label="Add Haul Ticket"
          @click="onAdd"
          class="q-mb-md"
        />
        <q-btn
          color="primary"
          label="Edit Selected"
          @click="openBulkEditModal"
          class="q-mb-md"
          :disable="selectedRows.length === 0"
          :class="{ 'q-opacity-50': selectedRows.length === 0 }"
        />
        <q-btn
          color="red"
          label="Delete Selected"
          @click="openBulkDeleteModal"
          class="q-mb-md"
          :disable="selectedRows.length === 0"
          :class="{ 'q-opacity-50': selectedRows.length === 0 }"
        />
        <q-btn
          color="primary"
          outline
          :label="allCurrentPageSelected ? 'Deselect All' : 'Select All'"
          @click="toggleSelectAll"
          class="q-mb-md"
          :disable="currentPageTicketIds.length === 0"
        />
      </div>
    </div>

    <!-- Data Table -->
    <BaseTable
      :rows="formattedHaulTickets"
      :columns="columns"
      :total-rows="totalHaulTickets"
      search-placeholder="Search Current Table..."
      @visible-rows-change="onVisibleRowsChange"
    >
      <!-- "actions" slot: Checkbox + single-row Edit/Delete -->
      <template #actions="{ row }">
        <q-td>
          <q-checkbox
            class="q-mr-sm"
            v-model="selectedRows"
            :val="row.id"
            dense
          />
          <q-btn
            size="xs"
            color="primary"
            icon="edit"
            @click="onEdit(row)"
            class="q-mr-sm"
          />
          <q-btn
            size="xs"
            color="negative"
            icon="delete"
            @click="openDeleteDialog(row.id)"
          />
        </q-td>
      </template>
    </BaseTable>

    <!-- Pagination Control (bottom of table) -->
    <div class="row justify-left q-my-md q-ml-lg">
      <q-pagination
        v-model="currentPage"
        :max="totalPages"
        @update:model-value="onPageChange"
        color="primary"
        direction-links
        :max-pages="10"
      />
    </div>

    <!-- Single-row Delete Confirmation Dialog -->
    <q-dialog v-model="showDeleteDialog">
      <q-card>
        <q-card-section class="text-h6">
          Confirm Deletion
        </q-card-section>

        <q-card-section>
          Are you sure you want to delete this haul ticket? This action cannot be undone.
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            flat
            label="Cancel"
            color="primary"
            v-close-popup
          />
          <q-btn
            flat
            label="Delete"
            color="negative"
            @click="deleteItem"
            v-close-popup
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Bulk Edit Modal -->
    <q-dialog v-model="showBulkEditModal">
      <q-card class="q-pa-md">
        <q-card-section>
          <div class="text-h6">Editing {{ selectedRows.length }} Haul Ticket(s)</div>
        </q-card-section>

        <q-form ref="editFormRef" @submit.prevent="submitEditForm">
          <q-card-section>
            <!-- Customer -->
            <q-select
              v-model="editForm.customer_id"
              :options="customerOptions"
              map-options
              option-value="value"
              option-label="label"
              label="Customer"
              outlined
              dense
            />
            <!-- Feedmill -->
            <q-select
              v-model="editForm.feedmill_id"
              :options="feedmillOptions"
              map-options
              option-value="value"
              option-label="label"
              label="Feedmill"
              outlined
              dense
              class="q-mt-md"
            />
            <!-- Driver -->
            <q-select
              v-model="editForm.driver_id"
              :options="driverOptions"
              map-options
              option-value="value"
              option-label="label"
              label="Driver"
              outlined
              dense
              class="q-mt-md"
            />
            <!-- Feed -->
            <q-select
              v-model="editForm.feed_id"
              :options="feedOptions"
              map-options
              option-value="value"
              option-label="label"
              label="Feed"
              outlined
              dense
              class="q-mt-md"
            />
            <!-- Rate -->
            <q-input
              v-model.number="editForm.rate"
              label="Rate"
              :rules="[validateNumeric]"
              outlined
              dense
              class="q-mt-md"
            />
          </q-card-section>

          <q-card-actions align="right">
            <q-btn
              label="Cancel"
              color="negative"
              @click="showBulkEditModal = false"
              flat
            />
            <q-btn
              label="Submit"
              type="submit"
              color="primary"
            />
          </q-card-actions>
        </q-form>
      </q-card>
    </q-dialog>

    <!-- Bulk Delete Confirmation Dialog -->
    <q-dialog v-model="bulkDeleteModalVisible">
      <q-card>
        <q-card-section>
          <div class="text-h6">Confirm Deletion</div>
        </q-card-section>

        <q-card-section>
          <p>Are you sure you want to delete <b>{{ selectedRows.length }}</b> ticket(s)? This action cannot be undone.</p>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            flat
            label="Cancel"
            color="primary"
            @click="cancelBulkDelete"
          />
          <q-btn
            flat
            label="Delete"
            color="negative"
            @click="confirmBulkDelete"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { watch } from 'vue'
import BaseTable from '@/components/BaseTable'
import { selectedYear } from '@/store/store'

export default {
  name: 'TicketHaulTable',
  components: {
    BaseTable
  },
  data() {
    return {
      // Table data
      haulTickets: [],
      totalHaulTickets: 0, // total items

      // Pagination state
      currentPage: 1,
      pageSize: 25,
      totalPages: 1,

      // Filters
      filters: {
        ticket_number: null,
        dairy_ticket_number: null,
        dateRange: null,
        customer_id: null,
        feedmill_id: null,
        driver_id: null,
        feed_id: null
      },

      // For date picker and dialogs
      showFilterDatePicker: false,
      showDeleteDialog: false,
      deleteId: null,

      // Bulk edit/delete
      selectedRows: [],
      visibleRowIds: null,
      showBulkEditModal: false,
      bulkDeleteModalVisible: false,

      // Bulk edit form
      editForm: {
        customer_id: null,
        feedmill_id: null,
        driver_id: null,
        feed_id: null,
        rate: null,
        ids: []
      },

      // Options for dropdowns
      customerOptions: [],
      feedmillOptions: [],
      driverOptions: [],
      feedOptions: [],

      // Table columns
      columns: [
        {
          name: 'actions',
          align: 'center',
          label: 'Actions',
          field: 'actions',
          sortable: false
        },
        { name: 'ticket_number', align: 'left', label: 'Ticket Number', field: 'ticket_number', sortable: true },
        { name: 'dairy_ticket_number', align: 'left', label: 'Dairy Ticket Number', field: 'dairy_ticket_number', sortable: true },
        { name: 'date', align: 'left', label: 'Date', field: 'date', sortable: true },
        { name: 'customer_id', align: 'left', label: 'Customer', field: 'customer_id', sortable: true },
        { name: 'feedmill_id', align: 'left', label: 'Feedmill', field: 'feedmill_id', sortable: true },
        { name: 'driver_id', align: 'left', label: 'Driver', field: 'driver_id', sortable: true },
        { name: 'feed_id', align: 'left', label: 'Feed', field: 'feed_id', sortable: true },
        { name: 'gross_weight', align: 'left', label: 'Gross Weight', field: 'gross_weight', sortable: true },
        { name: 'tare_weight', align: 'left', label: 'Tare Weight', field: 'tare_weight', sortable: true },
        { name: 'rate', align: 'left', label: 'Rate', field: 'rate', sortable: true }
      ]
    }
  },
  setup() {
    // Restrict date selection to only the selected year
    const restrictDates = (date) => {
      const year = selectedYear.value
      const start = `${year}/01/01`
      const end = `${year}/12/31`
      return date >= start && date <= end
    }
    return { restrictDates }
  },
  computed: {
    // Format the fetched haul tickets for display
    formattedHaulTickets() {
      return this.haulTickets.map(ticket => ({
        ...ticket,
        dairy_ticket_number: ticket.dairy_ticket_number || '',
        customer_id: ticket.customer_id?.name || 'N/A',
        feedmill_id: ticket.feedmill_id?.name || 'N/A',
        driver_id: ticket.driver_id?.name || 'N/A',
        feed_id: ticket.feed_id?.name || 'N/A',
        date: new Date(ticket.date).toLocaleDateString()
      }))
    },
    currentPageTicketIds() {
      if (Array.isArray(this.visibleRowIds)) {
        return this.visibleRowIds
      }

      return this.haulTickets
        .map(ticket => ticket.id)
        .filter(id => id !== undefined && id !== null)
    },
    allCurrentPageSelected() {
      return this.currentPageTicketIds.length > 0
        && this.currentPageTicketIds.every(id => this.selectedRows.includes(id))
    }
  },
  created() {
    this.fetchHaulTickets()
    this.fetchFilterOptions()

    // Watch the selectedYear if using Options API
    watch(selectedYear, () => {
      this.currentPage = 1
      this.fetchHaulTickets()
    })
  },
  methods: {
    // Grab dropdown options (customers, feedmills, drivers, feeds)
    async fetchFilterOptions() {
      try {
        const [customers, feedmills, drivers, feeds] = await Promise.all([
          axios.get('/api/v1/customer/customers/'),
          axios.get('/api/v1/feedmill/feedmills/'),
          axios.get('/api/v1/driver/drivers/'),
          axios.get('/api/v1/feed/feeds/')
        ])

        this.customerOptions = customers.data.map(item => ({
          label: item.name,
          value: item.id
        }))
        this.feedmillOptions = feedmills.data.map(item => ({
          label: item.name,
          value: item.id
        }))
        this.driverOptions = drivers.data.map(item => ({
          label: item.name,
          value: item.id
        }))
        this.feedOptions = feeds.data.map(item => ({
          label: item.name,
          value: item.id
        }))
      } catch (error) {
        console.error('Error fetching filter options:', error)
      }
    },

    // Fetch the Haul tickets with filters + pagination
    async fetchHaulTickets() {
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize,
          year: selectedYear.value,
          ...(this.filters.ticket_number && { ticket_number: this.filters.ticket_number }),
          ...(this.filters.dairy_ticket_number && { dairy_ticket_number: this.filters.dairy_ticket_number }),
          ...(this.filters.dateRange && {
            date_start: this.filters.dateRange.from,
            date_end: this.filters.dateRange.to
          }),
          ...(this.filters.customer_id && { customer_id: this.filters.customer_id.value }),
          ...(this.filters.feedmill_id && { feedmill_id: this.filters.feedmill_id.value }),
          ...(this.filters.driver_id && { driver_id: this.filters.driver_id.value }),
          ...(this.filters.feed_id && { feed_id: this.filters.feed_id.value })
        }

        const response = await axios.get('/api/v1/ticket_haul/ticket-hauls/', { params })
        // Using DRF pagination with count, total_pages, results, etc.
        this.haulTickets = response.data.results
        this.totalHaulTickets = response.data.count
        this.totalPages = response.data.total_pages
      } catch (error) {
        console.error('Error fetching Haul tickets:', error)
      }
    },

    // Pagination
    onPageChange(page) {
      this.currentPage = page
      this.fetchHaulTickets()
    },

    toggleSelectAll() {
      if (this.allCurrentPageSelected) {
        this.selectedRows = this.selectedRows.filter(
          id => !this.currentPageTicketIds.includes(id)
        )
        return
      }

      const selectedSet = new Set(this.selectedRows)
      this.currentPageTicketIds.forEach(id => selectedSet.add(id))
      this.selectedRows = Array.from(selectedSet)
    },

    onVisibleRowsChange(rows) {
      this.visibleRowIds = rows
        .map(row => row.id)
        .filter(id => id !== undefined && id !== null)
    },

    // Single ticket actions
    onAdd() {
      this.$router.push('/haul_ticket/add')
    },
    onEdit(row) {
      this.$router.push(`/haul_ticket/edit/${row.id}`)
    },
    openDeleteDialog(id) {
      this.deleteId = id
      this.showDeleteDialog = true
    },
    async deleteItem() {
      try {
        await axios.delete(`/api/v1/ticket_haul/ticket-hauls/${this.deleteId}/`)
        this.fetchHaulTickets()
      } catch (error) {
        console.error('Error deleting Haul ticket:', error)
      } finally {
        this.deleteId = null
      }
    },

    // Mass/bulk edit
    openBulkEditModal() {
      if (!this.selectedRows.length) return
      this.showBulkEditModal = true

      // Populate from first selected
      const firstSelected = this.haulTickets.find(t => t.id === this.selectedRows[0])
      if (!firstSelected) return

      this.editForm = {
        customer_id: firstSelected.customer_id?.id || null,
        feedmill_id: firstSelected.feedmill_id?.id || null,
        driver_id: firstSelected.driver_id?.id || null,
        feed_id: firstSelected.feed_id?.id || null,
        rate: firstSelected.rate ?? null,
        ids: []
      }
    },
    async submitEditForm() {
      this.$refs.editFormRef.validate().then(async (success) => {
        if (!success) return

        try {
          this.editForm.ids = this.selectedRows

          // Manually fix Quasar <q-select> data if needed
          const editSubmitData = { ...this.editForm }

          if (editSubmitData.customer_id && editSubmitData.customer_id.label) {
            editSubmitData.customer_id = editSubmitData.customer_id.value
          }
          if (editSubmitData.feedmill_id && editSubmitData.feedmill_id.label) {
            editSubmitData.feedmill_id = editSubmitData.feedmill_id.value
          }
          if (editSubmitData.driver_id && editSubmitData.driver_id.label) {
            editSubmitData.driver_id = editSubmitData.driver_id.value
          }
          if (editSubmitData.feed_id && editSubmitData.feed_id.label) {
            editSubmitData.feed_id = editSubmitData.feed_id.value
          }

          await axios.put('/api/v1/ticket_haul/ticket-hauls/bulk-update/', editSubmitData)

          this.$q.notify({
            color: 'green',
            message: 'Haul ticket(s) updated successfully!',
            icon: 'check'
          })
          this.showBulkEditModal = false
          this.fetchHaulTickets()
        } catch (error) {
          console.error('Error updating haul tickets:', error)
          this.$q.notify({
            color: 'red',
            message: 'Failed to update haul tickets.',
            icon: 'error'
          })
        }
      })
    },
    validateNumeric(value) {
      return (
        (value !== null && value !== '' && !isNaN(value)) ||
        'This field must be numeric'
      )
    },

    // Mass/bulk delete
    openBulkDeleteModal() {
      if (!this.selectedRows.length) {
        this.$q.notify({
          color: 'red',
          message: 'No haul tickets selected to delete.',
          icon: 'error'
        })
        return
      }
      this.bulkDeleteModalVisible = true
    },
    async confirmBulkDelete() {
      try {
        await axios.delete('/api/v1/ticket_haul/ticket-hauls/bulk-delete/', {
          data: {
            ids: this.selectedRows
          }
        })
        this.$q.notify({
          color: 'green',
          message: 'Haul ticket(s) deleted successfully!',
          icon: 'check'
        })
        this.fetchHaulTickets()
        this.selectedRows = []
      } catch (error) {
        console.error('Error deleting haul tickets:', error)
        this.$q.notify({
          color: 'red',
          message: 'Failed to delete haul tickets.',
          icon: 'error'
        })
      } finally {
        this.bulkDeleteModalVisible = false
      }
    },
    cancelBulkDelete() {
      this.bulkDeleteModalVisible = false
    },

    // Clear out all filters
    clearFilters() {
      this.filters = {
        ticket_number: null,
        dairy_ticket_number: null,
        dateRange: null,
        customer_id: null,
        feedmill_id: null,
        driver_id: null,
        feed_id: null
      }
      this.currentPage = 1
      this.fetchHaulTickets()
    }
  }
}
</script>

<style scoped>
/* Add your scoped CSS if needed */
</style>
